import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Plus, X, Check, Edit2, Trash2, ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Tool } from '../types';

type EntityType = 'tools';
type Entity = Tool;

interface EntityEditorProps {
  type: EntityType;
  entities: Entity[];
  onRefresh: () => void;
}

interface StaffSettingsProps {
  onBack: () => void;
  onUpdate?: () => void;
}

function EntityEditor({ type, entities, onRefresh }: EntityEditorProps) {
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleAdd = async () => {
    try {
      const { error: addError } = await supabase
        .from(type)
        .insert({ name: newName });

      if (addError) throw addError;
      
      setNewName('');
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add entity');
    }
  };

  const handleEdit = async (id: string) => {
    try {
      const { error: updateError } = await supabase
        .from(type)
        .update({ name: editingName })
        .eq('id', id);

      if (updateError) throw updateError;
      
      setEditingId(null);
      setEditingName('');
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update entity');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from(type)
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;
      
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete entity');
    }
  };

  const startEdit = (entity: Entity) => {
    setEditingId(entity.id);
    setEditingName(entity.name);
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded-md">
          {error}
        </div>
      )}

      <div className="flex gap-2">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder={`Add new ${type.slice(0, -1)}...`}
          className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
        />
        <button
          onClick={handleAdd}
          disabled={!newName.trim()}
          className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
        >
          <Plus className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-2">
        {entities.map((entity) => (
          <div
            key={entity.id}
            className="flex items-center justify-between gap-2 p-2 bg-white rounded-md shadow-sm"
          >
            {editingId === entity.id ? (
              <>
                <input
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(entity.id)}
                    className="p-1 text-green-600 hover:text-green-700"
                  >
                    <Check className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setEditingId(null)}
                    className="p-1 text-gray-600 hover:text-gray-700"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </>
            ) : (
              <>
                <span className="flex-1">{entity.name}</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => startEdit(entity)}
                    className="p-1 text-gray-600 hover:text-gray-700"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(entity.id)}
                    className="p-1 text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function StaffSettings({ onBack, onUpdate }: StaffSettingsProps) {
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data: toolsData, error: toolsError } = await supabase
        .from('tools')
        .select('*')
        .order('name');

      if (toolsError) throw toolsError;
      setTools(toolsData);
      onUpdate?.();
    } catch (error) {
      console.error('Error loading settings data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center">
              <SettingsIcon className="w-8 h-8 text-indigo-600 mr-3" />
              <h1 className="text-3xl font-bold text-gray-900">Staff Settings</h1>
            </div>
            <button
              onClick={onBack}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Staff
            </button>
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Tools & Technologies</h2>
              <EntityEditor
                type="tools"
                entities={tools}
                onRefresh={loadData}
              />
            </div>
          </div>

          <div className="mt-8 bg-white shadow rounded-lg overflow-hidden">
            <div className="p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Experience Levels</h2>
              <div className="space-y-2">
                {['junior', 'mid-level', 'senior', 'lead'].map((level) => (
                  <div
                    key={level}
                    className="p-2 bg-gray-50 rounded-md text-gray-700 flex items-center"
                  >
                    <span className="capitalize">{level}</span>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-sm text-gray-500">
                Experience levels are predefined and cannot be modified to maintain consistency.
              </p>
            </div>
          </div>

          <div className="mt-8 bg-white shadow rounded-lg overflow-hidden">
            <div className="p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Employment Status Types</h2>
              <div className="space-y-2">
                {[
                  { value: 'w2', label: 'W2' },
                  { value: '1099_full_time', label: '1099 Full Time' },
                  { value: '1099_part_time', label: '1099 Part Time' }
                ].map((status) => (
                  <div
                    key={status.value}
                    className="p-2 bg-gray-50 rounded-md text-gray-700 flex items-center"
                  >
                    <span>{status.label}</span>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-sm text-gray-500">
                Employment status types are predefined and cannot be modified to maintain consistency.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}